import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DateTime } from "luxon";
import { Container } from "../components/Container";
import { Card } from "../components/Card";
import { Input } from "../components/Input";
import { Button } from "../components/Button";
import { InfoBox } from "../components/InfoBox";
import { EmptyState } from "../components/EmptyState";
import { LoadingContainer } from "../components/LoadingContainer";
import { SectionTitle } from "../components/SectionTitle";
import { Title } from "../components/Title";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../components/Table";
import { KickbackSourceCard } from "./components/kickback/KickbackSourceCard";
import { KickbackUnmatchedTable } from "./components/kickback/KickbackUnmatchedTable";
import { totalsByReseller } from "./components/kickback/kickbackTotals";
import {
  useCalculateVivaKickbackMutation,
  useGetKickbackPeriodQuery,
  useSetKickbackCompanyMatchMutation,
  useUploadKickbackReportMutation,
} from "../query/kickbacksQuery";
import { useGetUserQuery } from "../query/usersQuery";
import { useStoreSearchKeyQuery } from "../query/storesQuery";
import { formatMoney } from "../utils/formatMoney";
import { spacing } from "@styling/sizing";
import {
  KICKBACK_REPORT_SOURCES,
  KickbackSource,
} from "@KassaroAB/shared/types/enums/KickbackSource";

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x4}px;
`;

const PeriodLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const PeriodInput = styled(Input)`
  width: auto;
`;

const LoadError = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.x2}px;
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x4}px;
`;

const SourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing.x3}px;
`;

const currentMonth = () => DateTime.now().toFormat("yyyy-MM");

export const KickbackView = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(currentMonth);
  const { data: user } = useGetUserQuery();
  const searchConfig = useStoreSearchKeyQuery(user, true).data ?? null;
  const {
    data: overview,
    isLoading,
    error: loadError,
    refetch,
  } = useGetKickbackPeriodQuery(period);
  const calculateViva = useCalculateVivaKickbackMutation(period);
  const uploadReport = useUploadKickbackReportMutation(period);
  const setMatch = useSetKickbackCompanyMatchMutation(period);

  const reports = overview?.sources ?? [];
  const resellerTotals = totalsByReseller(reports);
  const error = calculateViva.error ?? uploadReport.error ?? setMatch.error;

  return (
    <Container>
      <PageHeader>
        <Title>{t("kickback.title")}</Title>
        <PeriodLabel>
          {t("kickback.period")}
          <PeriodInput
            type="month"
            value={period}
            max={currentMonth()}
          onChange={(event) => {
            if (event.target.value) {
              setPeriod(event.target.value);
            }
          }}
          />
        </PeriodLabel>
      </PageHeader>

      <Sections>
        <InfoBox variant="info">{t("kickback.description")}</InfoBox>
        {error && <InfoBox variant="error">{error.message}</InfoBox>}

        {isLoading ? (
          <LoadingContainer message={t("kickback.loading")} />
        ) : loadError ? (
          <InfoBox variant="error">
            <LoadError>
              <span>
                {t("kickback.loadError")} {loadError.message}
              </span>
              <Button $variant="outlined" onClick={() => refetch()}>
                {t("common.retry")}
              </Button>
            </LoadError>
          </InfoBox>
        ) : (
          <>
            <SourceGrid>
              <KickbackSourceCard
                kind="viva"
                source={KickbackSource.VIVA}
                invoicePeriod={period}
                report={reports.find((report) => report.source === KickbackSource.VIVA)}
                isPending={calculateViva.isPending}
                onCalculate={(refresh) => calculateViva.mutate(refresh)}
              />
              {KICKBACK_REPORT_SOURCES.map((source) => (
                <KickbackSourceCard
                  key={source}
                  kind="report"
                  source={source}
                  invoicePeriod={period}
                  report={reports.find((report) => report.source === source)}
                  isPending={uploadReport.isPending && uploadReport.variables?.source === source}
                  onUpload={(file) => uploadReport.mutate({ source, file })}
                />
              ))}
            </SourceGrid>

            <KickbackUnmatchedTable
              reports={reports}
              searchConfig={searchConfig}
              disabled={setMatch.isPending}
              onMatch={(source, reference, storeId) =>
                setMatch.mutate({ source, reference, storeId })
              }
            />

            <Card>
              <SectionTitle>{t("kickback.perResellerTitle")}</SectionTitle>
              <p>{t("kickback.perResellerDescription")}</p>
              {resellerTotals.length === 0 ? (
                <EmptyState message={t("kickback.noResellerTotals")} />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>{t("kickback.reseller")}</TableHeaderCell>
                      <TableHeaderCell align="right">{t("kickback.rows")}</TableHeaderCell>
                      <TableHeaderCell align="right">{t("kickback.profitShare")}</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {resellerTotals.map((total) => (
                      <TableRow
                        key={total.resellerId}
                        to={`/resellers/${total.resellerId}/invoicing`}
                      >
                        <TableCell>{total.resellerName}</TableCell>
                        <TableCell align="right">{total.rowCount}</TableCell>
                        <TableCell align="right" noWrap>
                          {formatMoney(total.profitShare)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </>
        )}
      </Sections>

    </Container>
  );
};
